<template>
  <div class="assign-roles-page pa-10">

    <!-- ── Header ── -->
    <div class="mb-7">
      <div class="page-title">Assign User Roles</div>
      <div class="page-sub mt-1">Manage user roles and program assignments</div>
    </div>

    <!-- ── Stat Cards ── -->
    <v-row class="mb-6" style="gap: 0;">
      <v-col cols="12" sm="4">
        <div class="stat-card rounded-xl pa-5" style="background: #eef5fe; border-color: #c7ddff;">
          <div class="stat-icon-box rounded-lg pa-2 mb-4" style="background: #c7ddff; display: inline-flex;">
            <v-icon size="22" color="#1C4EE7">mdi-account-multiple-outline</v-icon>
          </div>
          <div class="stat-number" style="color: #1c398e;">{{ getTotalUsers }}</div>
          <div class="stat-label" style="color: #1483f0;">Total Users</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card rounded-xl pa-5" style="background: #edfcf2; border-color: #bff2d3;">
          <div class="stat-icon-box rounded-lg pa-2 mb-4" style="background: #bff2d3; display: inline-flex;">
            <v-icon size="22" color="#05853A">mdi-account-cog-outline</v-icon>
          </div>
          <div class="stat-number" style="color: #1c398e;">{{ getTotalAssignedRoles }}</div>
          <div class="stat-label" style="color: #078267;">Assigned Roles</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card rounded-xl pa-5" style="background: #f9f4ff; border-color: #ebd2ff;">
          <div class="stat-icon-box rounded-lg pa-2 mb-4" style="background: #ebd2ff; display: inline-flex;">
            <v-icon size="22" color="#972AE2">mdi-book-open-blank-variant-outline</v-icon>
          </div>
          <div class="stat-number" style="color: #1c398e;">{{ getTotalProgramsAssigned }}</div>
          <div class="stat-label" style="color: #972ae2;">Program Assigned</div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Search ── -->
    <v-row class="mb-2" style="gap:0;">
      <v-col cols="12" sm="5" md="4">
        <v-text-field
          v-model="search"
          placeholder="Search users by name or email..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          bg-color="white"
          class="search-field"
        />
      </v-col>
    </v-row>

    <!-- ── Table ── -->
    <v-card class="table-card rounded-xl" elevation="0">
      <v-data-table
        :headers="headers"
        :items="data"
        :search="search"
        :loading="loading"
        hover
        class="assign-table"
      >
        <!-- Custom header -->
        <template #headers="{ columns }">
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              class="table-th"
              :style="col.align === 'center' ? 'text-align: center' : ''"
            >
              {{ col.title }}
            </th>
          </tr>
        </template>

        <!-- Name -->
        <template #[`item.name`]="{ item }">
          <span class="row-name">{{ item.name }}</span>
        </template>

        <!-- Email -->
        <template #[`item.email`]="{ item }">
          <span class="row-email">{{ item.email }}</span>
        </template>

        <!-- Assigned Roles -->
        <template #[`item.roles`]="{ item }">
          <template v-if="item.roles && item.roles.length">
            <v-chip
              v-for="(role, idx) in item.roles"
              :key="idx"
              size="small"
              class="role-chip mr-1 my-1"
            >
              {{ role }}
            </v-chip>
          </template>
          <span v-else class="empty-text">No roles assigned</span>
        </template>

        <!-- Program -->
        <template #[`item.programs_assigned`]="{ item }">
          <template v-if="item.programs_assigned && item.programs_assigned.length">
            <v-chip
              v-for="(prog, idx) in item.programs_assigned"
              :key="idx"
              size="small"
              class="prog-chip mr-1 my-1"
            >
              {{ prog }}
            </v-chip>
          </template>
          <span v-else class="empty-text">Not assigned</span>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{ item }">
          <v-btn
            size="small"
            color="#10b981"
            variant="flat"
            rounded="lg"
            class="text-white assign-btn"
            @click="viewItem(item)"
          >
            Assign Role
          </v-btn>
        </template>

        <template #no-data>
          <div class="text-center text-grey py-8">No users found.</div>
        </template>
      </v-data-table>
    </v-card>

    <AssignRoleDialog :data="itemData" />

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import eventBus from "@/eventBus";
import AssignRoleDialog from "../Dialogs/Forms/AssignRoleDialog.vue";

export default {
  components: { AssignRoleDialog },

  data: () => ({
    search: "",
    headers: [
      { title: "Name",           value: "name",              align: "start",  sortable: true  },
      { title: "Email",          value: "email",             align: "start",  sortable: true  },
      { title: "Assigned Roles", value: "roles",             align: "start",  sortable: false },
      { title: "Program",        value: "programs_assigned", align: "start",  sortable: false },
      { title: "Actions",        value: "actions",           align: "center", sortable: false },
    ],
    data: [],
    itemData: null,
    loading: false,
    action: null,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "Successfully Saved!",
      message: "",
      top: 10,
    },
    program_list: [],
  }),

  mounted() {
    this.getProgram();
    this.initialize();
    eventBus.on("closeAssignRoleDialog", () => { this.initialize(); });
  },

  beforeUnmount() {
    eventBus.off("closeAssignRoleDialog");
  },

  computed: {
    getTotalUsers() {
      return this.data.length;
    },
    getTotalAssignedRoles() {
      return this.data.filter((item) => item.roles && item.roles.length > 0).length;
    },
    getTotalProgramsAssigned() {
      return this.data.filter((item) => item.assigned_as_instructor_id && item.assigned_as_instructor_id.length > 0).length;
    },
  },

  methods: {
    initialize() {
      this.loading = true;
      this.axiosCall("/user-details/getUsersUnderMyInst", "GET").then((res) => {
        if (res) this.data = res.data;
        this.loading = false;
      });
    },
    getProgram() {
      this.axiosCall("/program/getProgramsByInstitute", "GET").then((res) => {
        if (res) this.program_list = res.data;
      });
    },
    viewItem(item) {
      this.itemData = item;
      this.action = "View";
    },
  },
};
</script>

<style scoped>
.assign-roles-page {
  min-height: 100vh;
}

/* ── Header ── */
.page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
}
.page-sub {
  font-size: 14px;
  color: #6b7280;
}

/* ── Stat Cards ── */
.stat-card {
  border: 1px solid transparent;
  transition: box-shadow 0.2s;
  height: 100%;
}
.stat-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}
.stat-number {
  font-size: 40px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -1.5px;
}
.stat-label {
  font-size: 15px;
  font-weight: 700;
  margin-top: 4px;
}

/* ── Search ── */
.search-field :deep(.v-field) {
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.search-field :deep(.v-field__outline__start),
.search-field :deep(.v-field__outline__notch),
.search-field :deep(.v-field__outline__end) {
  border-color: #e2e8f0 !important;
}

/* ── Table Card ── */
.table-card {
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* ── Custom Table Header ── */
.table-th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em !important;
  text-transform: uppercase !important;
  color: #6b7280 !important;
  background: #ffffff !important;
  border-bottom: 1px solid #f3f4f6 !important;
  padding: 14px 16px !important;
  white-space: nowrap;
}

/* ── Row Cells ── */
.row-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.row-email {
  font-size: 13px;
  color: #6b7280;
}
.row-text {
  font-size: 13px;
  color: #374151;
}
.empty-text {
  font-size: 13px;
  font-style: italic;
  color: #9ca3af;
}

/* ── Chips ── */
.role-chip {
  background: #e0e7ff !important;
  color: #3730a3 !important;
  font-size: 12px !important;
  font-weight: 500 !important;
}
.prog-chip {
  background: #d1fae5 !important;
  color: #065f46 !important;
  font-size: 12px !important;
  font-weight: 500 !important;
}

/* ── Button ── */
.assign-btn {
  font-size: 12.5px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
}

/* ── Table row hover ── */
.assign-table :deep(tbody tr:hover td) {
  background: #f9fafb;
}
.assign-table :deep(tbody td) {
  font-size: 13px;
  border-bottom: 1px solid #f3f4f6 !important;
  padding: 14px 16px;
}
</style>
